import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useCopyJobs } from '@/lib/copy-jobs/context';
import { formatBytes } from '@/lib/format';

/**
 * A small header badge that shows the count of active copy jobs.
 * Clicking it opens a popover with per-job progress.
 * Disappears automatically when no jobs are active.
 * Survives page refresh — polls active jobs from the server.
 */
export function CopyJobsBadge() {
  const { t } = useTranslation();
  const { activeJobs } = useCopyJobs();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (activeJobs.length === 0) return null;

  const totalBytesCopied = activeJobs.reduce((s, j) => s + j.bytes_copied, 0);
  const totalBytesExpected = activeJobs.reduce((s, j) => s + (j.total_bytes ?? 0), 0);
  const running = activeJobs.filter((j) => j.status === 'running');
  const queued = activeJobs.filter((j) => j.status === 'queued');

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={t('copyBadge.title', { count: activeJobs.length })}
        className={cn(
          'relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium',
          'bg-accent/10 text-accent hover:bg-accent/20 transition-colors',
        )}
      >
        <Copy className="w-3.5 h-3.5 animate-pulse" />
        <span>{activeJobs.length}</span>
        <span className="hidden sm:inline">{t('copyBadge.copying')}</span>
        {totalBytesCopied > 0 && (
          <span className="hidden sm:inline text-accent/70">
            {formatBytes(totalBytesCopied)}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 rounded-lg border border-mist bg-white shadow-lg z-50">
          <div className="px-4 py-3 border-b border-mist">
            <p className="text-sm font-medium text-ink">
              {t('copyBadge.title', { count: activeJobs.length })}
            </p>
            <p className="text-xs text-slate mt-0.5">
              {running.length} {t('copyBadge.running')}, {queued.length} {t('copyBadge.queued')}
              {totalBytesCopied > 0 && ` · ${formatBytes(totalBytesCopied)}`}
              {totalBytesExpected > 0 && ` / ${formatBytes(totalBytesExpected)}`}
            </p>
            {/* Overall progress bar */}
            <div className="mt-2 h-1.5 w-full rounded-full bg-mist overflow-hidden">
              {totalBytesExpected > 0 ? (
                <div
                  className="h-full rounded-full bg-accent transition-all duration-500"
                  style={{ width: `${Math.min(100, Math.round((totalBytesCopied / totalBytesExpected) * 100))}%` }}
                />
              ) : (
                <div className="h-full w-1/3 rounded-full bg-accent/60 animate-[shimmer_1.5s_ease-in-out_infinite]" />
              )}
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto p-2 flex flex-col gap-1">
            {activeJobs.map((job) => {
              const pct = job.total_bytes && job.total_bytes > 0
                ? Math.min(100, Math.round((job.bytes_copied / job.total_bytes) * 100))
                : null;
              return (
                <div key={job.job_id} className="px-2 py-1.5 rounded hover:bg-surface">
                  <div className="flex items-center gap-2 text-xs">
                    <span
                      className={cn(
                        'w-1.5 h-1.5 rounded-full shrink-0',
                        job.status === 'running' ? 'bg-accent animate-pulse' : 'bg-slate/30',
                      )}
                    />
                    <span className="font-mono truncate flex-1">
                      {job.remote_path.split('/').pop() || job.remote_path}
                    </span>
                    <span className="text-slate shrink-0">
                      {job.bytes_copied > 0 && formatBytes(job.bytes_copied)}
                      {pct !== null && ` (${pct}%)`}
                    </span>
                  </div>
                  {/* Per-job progress bar */}
                  {job.status === 'running' && (
                    <div className="mt-1 ml-3.5 h-1 rounded-full bg-mist overflow-hidden">
                      {pct !== null ? (
                        <div
                          className="h-full rounded-full bg-accent/70 transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      ) : (
                        <div className="h-full w-1/4 rounded-full bg-accent/40 animate-[shimmer_1.5s_ease-in-out_infinite]" />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
